
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Briefcase, Truck, ChevronRight, Layers } from 'lucide-react';
import { DOMAIN } from '../constants';
import { Reveal, SectionHero } from '../components/UI';

export const Services: React.FC = () => {
  const pageTitle = "Services de Bétonnage & Produits de Maçonnerie Granby | Clé en Main";
  const pageDesc = "Services complets : Béton mobile, planchers radiants, location d'outils, produits en vrac (pierre, sable). Projets clés en main disponibles.";
  const canonicalUrl = `${DOMAIN}/services`;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Services et Produits Béton Mobile des Cantons",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Béton Mobile" },
      { "@type": "ListItem", "position": 2, "name": "Plancher Radiant" },
      { "@type": "ListItem", "position": 3, "name": "Location d'équipement" },
      { "@type": "ListItem", "position": 4, "name": "Vente de Produits en Vrac" }
    ]
  };

  return (
    <div className="bg-slate-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1535732759880-bbd5c7265e3f?q=80&w=2064&auto=format&fit=crop" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1535732759880-bbd5c7265e3f?q=80&w=2064&auto=format&fit=crop" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <SectionHero
        title="Réaliser vos projets de rêve"
        subtitle="Services & Produits"
        image="https://images.unsplash.com/photo-1535732759880-bbd5c7265e3f?q=80&w=2064&auto=format&fit=crop"
      />
      <div className="container mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-12 gap-12 mb-20">
           <div className="lg:col-span-8">
             <Reveal>
               <h2 className="text-3xl font-display font-bold text-slate-900 mb-6 uppercase flex items-center gap-3">
                 <span className="w-12 h-2 bg-brand-red"></span> Soutien Technique Complet
               </h2>
               <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-4">
                 <p className="text-lg font-medium text-slate-800">Si vous voulez le faire vous-même et économiser, Béton Mobile des Cantons vous fournit le soutien technique, les outils et le coffrage.</p>
                 <p>Notre entreprise peut même vous louer un laser ou une remorque pour le transport des matériaux et des équipements.</p>
                 <p>Nous mettons à votre disposition des coffrages à béton de tous genres pour construire des murs de fondations, des dalles, des terrasses suspendues et des trottoirs, en plus de fournir les outils nécessaires à la mise en place du béton (truelle, règle, râteau, etc.) Et, pour vous faciliter la tâche, nous avons aménagé un magasin à même l'entreprise, où nous faisons la vente de produits connexes tels que les armatures d'acier, les ancrages, les scellants et la colle à béton, l'isolant, les sonotubes, etc. Nous avons également une salle d'exposition et des conseillers sur place afin de mener à terme vos projets.</p>
                 <p>Un service 24 heures sur 24 et 7 jours sur 7 est même disponible à la demande lors de la réalisation de chantiers.</p>
                 <p className="font-bold text-slate-900">Vous êtes en de bonnes mains avec Béton Mobile des Cantons. Nous avons réalisé plus de 25 000 projets depuis plus de 25 ans.</p>
               </div>
             </Reveal>
           </div>
           <div className="lg:col-span-4">
              <div className="bg-slate-900 text-white p-8 relative overflow-hidden h-full shadow-2xl border-b-4 border-brand-red">
                 <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
                 <div className="relative z-10 flex flex-col h-full justify-center">
                    <div className="mb-6">
                      <div className="w-16 h-16 bg-brand-red rounded-full flex items-center justify-center mb-6 shadow-lg"><Briefcase size={32} className="text-white" /></div>
                      <h3 className="text-3xl font-display font-bold uppercase mb-4 text-white">Clé en main</h3>
                      <div className="w-12 h-1 bg-brand-red mb-6"></div>
                      <p className="text-slate-300 font-light text-lg leading-relaxed">Nous travaillons avec des professionnels d'expérience qui réaliseront vos projets avec succès.</p>
                    </div>
                    <Link to="/contact" className="w-full bg-white text-slate-900 text-center py-4 font-bold uppercase hover:bg-brand-red hover:text-white transition-all duration-300">Demander une soumission</Link>
                 </div>
              </div>
           </div>
        </div>
        <div className="grid md:grid-cols-2 gap-0 shadow-xl border border-slate-200">
           <Reveal>
             <div className="bg-white p-10 h-full border-r border-slate-200">
                <div className="flex items-center gap-4 mb-8">
                   <div className="p-3 bg-slate-100 text-brand-red"><Truck size={24} /></div>
                   <h3 className="text-2xl font-display font-bold text-slate-900 uppercase tracking-wide">Locations</h3>
                </div>
                <ul className="space-y-4 font-mono text-sm text-slate-700">
                   {["Location de remorque 4 X 8, 5 X 10 et une plateforme de 8 X 16", "Location de laser pour prendre le niveau", "Produits de scellant disponible en magasin pour la surface du béton & colle à béton", "Sonotube, isolant pour le béton", "Salle de montre pour faire un produit de qualité supérieur, assistance technique et outil fourni."].map((item, i) => (
                     <li key={i} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0"><ChevronRight className="text-brand-red shrink-0 mt-0.5" size={16}/><span>{item}</span></li>
                   ))}
                </ul>
             </div>
           </Reveal>
           <Reveal delay={200}>
             <div className="bg-slate-50 p-10 h-full">
                <div className="flex items-center gap-4 mb-8">
                   <div className="p-3 bg-white border border-slate-200 text-brand-red"><Layers size={24} /></div>
                   <h3 className="text-2xl font-display font-bold text-slate-900 uppercase tracking-wide">Produits</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {["Poussière de roche", "Terre", "Sable", "Pierre 5/14", "Pierre 1/4", "Pierre 0-3/4", "Pierre 3/4 net", "Pierre blanche", "Acier", "Scellant", "Une vaste gamme de produit isolant"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-200 last:border-0 border-dashed"><div className="w-1.5 h-1.5 bg-brand-red rounded-sm shrink-0"></div><span className="text-sm font-mono text-slate-700 font-medium">{item}</span></div>
                  ))}
                </div>
             </div>
           </Reveal>
        </div>
      </div>
    </div>
  );
};
